/**
 * BulkEnrolDialog — "Enrol whole batch into…": every person on this roster
 * joins another batch in one click (e.g. closed NHT cohort → new Global batch).
 * Shows a live preview (dry-run) before committing; source history is kept.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Users } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

export default function BulkEnrolDialog({ open, onOpenChange, batch, otherBatches, onDone }) {
  const { post } = useApi();
  const [target, setTarget] = useState('');
  const [makeCurrent, setMakeCurrent] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setTarget(''); setPreview(null); setMakeCurrent(true); } }, [open]);

  useEffect(() => {
    if (!target) { setPreview(null); return; }
    let live = true;
    post(`/trainees/batches/${batch.batch_id}/enrol-into?dry_run=true`, { target_batch_id: target })
      .then(r => { if (live) setPreview(r); })
      .catch(() => { if (live) setPreview(null); });
    return () => { live = false; };
  }, [target, batch.batch_id, post]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await post(`/trainees/batches/${batch.batch_id}/enrol-into`,
        { target_batch_id: target, make_current: makeCurrent });
      toast.success(`${r.enrolled} agent${r.enrolled === 1 ? '' : 's'} enrolled into ${r.target_name}`
        + (r.already_enrolled ? ` (${r.already_enrolled} already there)` : ''));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk enrol failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-enrol-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Enrol whole batch into…</DialogTitle>
          <DialogDescription>
            Everyone on <strong>{batch.name}</strong> joins the batch you pick. Nobody is removed from
            here — their {batch.name} history stays intact.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Target batch</Label>
            <Select value={target || '__none__'} onValueChange={v => setTarget(v === '__none__' ? '' : v)}>
              <SelectTrigger data-testid="bulk-enrol-target-select"><SelectValue placeholder="Pick a batch…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— pick —</SelectItem>
                {otherBatches.map(b => (
                  <SelectItem key={b.batch_id} value={b.batch_id}>
                    {b.code ? `${b.code} — ` : ''}{b.name}{b.department ? ` (${b.department})` : ''}
                    {b.status !== 'active' ? ` · ${b.status}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={makeCurrent}
                   onChange={e => setMakeCurrent(e.target.checked)} data-testid="bulk-enrol-make-current" />
            <span>Make it their current batch
              <span className="block text-xs text-muted-foreground">Dashboards, readiness and notifications follow the current batch.</span>
            </span>
          </label>
          {preview && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="bulk-enrol-preview">
              <p><strong>{preview.enrolled}</strong> of {preview.total} agents will be enrolled into <strong>{preview.target_name}</strong>.</p>
              {preview.already_enrolled > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{preview.already_enrolled} already enrolled there — skipped.</p>
              )}
              {preview.target_status && preview.target_status !== 'active' && (
                <p className="text-xs text-amber-700 mt-1">Target batch is {preview.target_status}; enrollments will be recorded as completed.</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} disabled={!target || busy || !preview || preview.enrolled === 0}
                  data-testid="bulk-enrol-confirm-btn">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            Enrol {preview?.enrolled ?? ''} agent{preview?.enrolled === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
